import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { SlidersHorizontal } from "lucide-react";
import { API, IMG, WA_DEFAULT } from "../lib/site";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading } from "../components/Reveal";
import { ListingCard } from "../components/ListingCard";

const BHK_OPTIONS = ["All", "1", "2", "3", "4"];
const BUDGETS = [
  { label: "Any Budget", min: 0, max: Infinity },
  { label: "Under ₹50 Lakh", min: 0, max: 50 },
  { label: "₹50 – 75 Lakh", min: 50, max: 75 },
  { label: "₹75 Lakh – 1 Cr", min: 75, max: 100 },
  { label: "₹1 – 2 Cr", min: 100, max: 200 },
  { label: "Above ₹2 Cr", min: 200, max: Infinity },
];

const inputCls =
  "h-12 border border-navy-900/15 bg-white px-4 text-sm text-navy-900 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/25";

export default function Resale() {
  const [listings, setListings] = useState([]);
  const [searchParams] = useSearchParams();
  const [bhk, setBhk] = useState("All");
  const [budgetIdx, setBudgetIdx] = useState(0);
  const [location, setLocation] = useState(searchParams.get("location") || "");

  useEffect(() => {
    axios.get(`${API}/listings`, { params: { category: "resale" } }).then((r) => setListings(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const b = BUDGETS[budgetIdx];
    return listings.filter((l) => {
      if (bhk !== "All" && String(l.bhk) !== bhk) return false;
      if (budgetIdx !== 0 && !(l.price_value > b.min && l.price_value <= b.max)) return false;
      if (location && !l.location.toLowerCase().includes(location.toLowerCase())) return false;
      return true;
    });
  }, [listings, bhk, budgetIdx, location]);

  return (
    <div data-testid="resale-page">
      <PageHero
        overline="Buy & Resale"
        title="Verified homes,"
        accent="honest prices."
        description="Ready-to-move resale flats across Noida Extension, Indirapuram, Vaishali and Wave City — every title checked before listing."
        image={IMG.living}
      />

      <section className="bg-white py-20 sm:py-24" data-testid="resale-catalog-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHeading
              overline="Resale Catalogue"
              title={<>Flats available <span className="italic text-gold-600">this week</span></>}
            />
            <Reveal delay={0.1}>
              <p className="text-sm text-gray-500" data-testid="resale-count">
                Showing <span className="font-bold text-navy-900">{filtered.length}</span> of {listings.length} homes
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-col gap-5 border border-navy-900/10 bg-cream p-5 sm:p-6" data-testid="resale-filters">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-navy-900/60">
                <SlidersHorizontal className="h-4 w-4 text-gold-600" />
                Refine your search
              </div>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="flex flex-wrap gap-2" data-testid="bhk-filters">
                  {BHK_OPTIONS.map((b) => (
                    <button
                      key={b}
                      data-testid={`bhk-filter-${b.toLowerCase()}`}
                      onClick={() => setBhk(b)}
                      className={`border px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] transition-[background-color,color,border-color] duration-300 ${
                        bhk === b
                          ? "border-navy-900 bg-navy-900 text-white"
                          : "border-navy-900/20 bg-white text-navy-900/60 hover:border-gold-500 hover:text-navy-900"
                      }`}
                    >
                      {b === "All" ? "All BHK" : `${b} BHK`}
                    </button>
                  ))}
                </div>
                <div className="flex flex-1 flex-col gap-4 sm:flex-row">
                  <select
                    data-testid="budget-filter-select"
                    value={budgetIdx}
                    onChange={(e) => setBudgetIdx(Number(e.target.value))}
                    className={`${inputCls} flex-1 cursor-pointer appearance-none`}
                  >
                    {BUDGETS.map((b, i) => (
                      <option key={b.label} value={i}>{b.label}</option>
                    ))}
                  </select>
                  <input
                    data-testid="location-filter-input"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Search locality — e.g. Vaishali"
                    className={`${inputCls} flex-1`}
                  />
                </div>
              </div>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="resale-grid">
            {filtered.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} />
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="mt-14 border border-dashed border-navy-900/20 bg-cream p-12 text-center" data-testid="resale-empty">
              <p className="font-serif text-2xl font-light text-navy-900">No exact match today.</p>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-gray-500">
                Our resale inventory turns over fast. Tell us your requirement on WhatsApp and we'll source it within 48 hours.
              </p>
              <a
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="resale-empty-wa-btn"
                className="mt-6 inline-flex items-center gap-2 bg-gold-500 px-6 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-navy-900 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-400"
              >
                Ask on WhatsApp
              </a>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
